import React, { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  fetchAllAgents,
  enableAgent,
  disableAgent,
  deleteAgent,
  AgentSummary,
  getAgent,
  enableIsPublicAgent,
  disableIsPublicAgent,
  createDisposableAgentLink,
  enableCanDisconnectAgent,
  disableCanDisconnectAgent,
} from "../../services/api";
import "./Admin.css";
import {
  HiEye,
  HiEyeOff,
  HiPlus,
  HiOutlineTag,
  HiOutlineLink,
  HiOutlineClipboard,
  HiOutlineX,
  HiCheck,
  HiPhone,
  HiOutlineShare,
} from "react-icons/hi";
import LoadingState from "../Common/LoadingState";

// Extend the AgentSummary interface to include phone_number
interface ExtendedAgentSummary extends AgentSummary {
  phone_number?: string | null;
  access_token?: string;
}

interface ProcessingState {
  uuid: string;
  action:
    | "enable"
    | "is_public"
    | "delete"
    | "duplicate"
    | "disposable"
    | "can_disconnect"
    | "copy_token"
    | "share_link";
}

interface DisposableLinkModal {
  isOpen: boolean;
  agentUuid: string | null;
  agentName: string;
  link: string;
  copied: boolean;
}

const AgentConfiguration: React.FC = () => {
  const navigate = useNavigate();
  const [agents, setAgents] = useState<ExtendedAgentSummary[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [hasMore, setHasMore] = useState<boolean>(true);
  const [offset, setOffset] = useState<number>(0);
  const [isFetchingMore, setIsFetchingMore] = useState<boolean>(false);
  const [processingStates, setProcessingStates] = useState<ProcessingState[]>(
    []
  );
  const [disposableLinkModal, setDisposableLinkModal] =
    useState<DisposableLinkModal>({
      isOpen: false,
      agentUuid: null,
      agentName: "",
      link: "",
      copied: false,
    });
  const [tokenCopied, setTokenCopied] = useState<{ [key: string]: boolean }>(
    {}
  );
  const [sharedAgent, setSharedAgent] = useState<string | null>(null);
  const sentinelRef = useRef<HTMLDivElement | null>(null);
  const didLoadRef = useRef<boolean>(false);
  const [search, setSearch] = useState<string>("");
  const [debouncedSearch, setDebouncedSearch] = useState<string>("");

  const PAGE_SIZE = 12;

  useEffect(() => {
    if (didLoadRef.current) return;
    didLoadRef.current = true;
    fetchInitialAgents("");
  }, []);

  // Debounce search input
  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search.trim()), 350);
    return () => clearTimeout(t);
  }, [search]);

  // When debounced search changes, reset and fetch from start
  useEffect(() => {
    fetchInitialAgents(debouncedSearch);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedSearch]);

  const fetchInitialAgents = async (searchTerm: string) => {
    try {
      setError(null);
      setLoading(true);
      const response = await fetchAllAgents(PAGE_SIZE, 0, searchTerm || undefined);
      setAgents(response.data || []);
      setHasMore(response.pagination?.has_more ?? false);
      const nextOffset = (response.pagination?.offset ?? 0) + (response.pagination?.limit ?? PAGE_SIZE);
      setOffset(nextOffset);
    } catch (err: any) {
      console.error("Error fetching agents:", err);
      setError(err.message || "Failed to fetch agents");
      setAgents([]);
      setHasMore(false);
      setOffset(0);
    } finally {
      setLoading(false);
    }
  };

  const loadMoreAgents = useCallback(async () => {
    if (loading || isFetchingMore || !hasMore) return;
    try {
      setIsFetchingMore(true);
      const response = await fetchAllAgents(PAGE_SIZE, offset, debouncedSearch || undefined);
      setAgents((prev) => [...prev, ...(response.data || [])]);
      setHasMore(response.pagination?.has_more ?? false);
      const increment = response.pagination?.limit ?? PAGE_SIZE;
      setOffset((prev) => prev + increment);
    } catch (err: any) {
      console.error("Error fetching more agents:", err);
      setError(err.message || "Failed to fetch more agents");
    } finally {
      setIsFetchingMore(false);
    }
  }, [loading, isFetchingMore, hasMore, offset, debouncedSearch]);

  useEffect(() => {
    const node = sentinelRef.current;
    if (!node) return;

    const observer = new IntersectionObserver(
      (entries) => {
        const first = entries[0];
        if (first.isIntersecting) {
          loadMoreAgents();
        }
      },
      { root: null, rootMargin: "200px", threshold: 0 }
    );

    observer.observe(node);
    return () => {
      observer.unobserve(node);
      observer.disconnect();
    };
  }, [loadMoreAgents]);

  const isProcessing = (
    agentUuid: string,
    action: ProcessingState["action"]
  ) => {
    return processingStates.some(
      (state) => state.uuid === agentUuid && state.action === action
    );
  };

  const addProcessingState = (
    agentUuid: string,
    action: ProcessingState["action"]
  ) => {
    setProcessingStates((prev) => [...prev, { uuid: agentUuid, action }]);
  };

  const removeProcessingState = (
    agentUuid: string,
    action: ProcessingState["action"]
  ) => {
    setProcessingStates((prev) =>
      prev.filter(
        (state) => !(state.uuid === agentUuid && state.action === action)
      )
    );
  };

  const handleToggleAgent = async (agentUuid: string, enabled: boolean) => {
    try {
      // Track processing state for this specific action
      addProcessingState(agentUuid, "enable");

      await (enabled ? enableAgent(agentUuid) : disableAgent(agentUuid));

      // Update the agents list with the updated enabled status
      setAgents((prev) =>
        prev.map((agent) =>
          agent.uuid === agentUuid ? { ...agent, enabled } : agent
        )
      );
    } catch (err: any) {
      console.error("Error toggling agent:", err);
      setError(
        err.message || `Failed to ${enabled ? "enable" : "disable"} agent`
      );
    } finally {
      // Clear processing state for this action
      removeProcessingState(agentUuid, "enable");
    }
  };

  const handleToggleIsPublic = async (agentUuid: string, isPublic: boolean) => {
    try {
      // Track processing state for this specific action
      addProcessingState(agentUuid, "is_public");

      await (!isPublic
        ? disableIsPublicAgent(agentUuid)
        : enableIsPublicAgent(agentUuid));

      // Update the agents list with the updated is_public status
      setAgents((prev) =>
        prev.map((agent) =>
          agent.uuid === agentUuid ? { ...agent, is_public: isPublic } : agent
        )
      );
    } catch (err: any) {
      console.error("Error toggling is_public mode:", err);
      setError(
        err.message ||
          `Failed to ${isPublic ? "disable" : "enable"} is_public mode`
      );
    } finally {
      // Clear processing state for this action
      removeProcessingState(agentUuid, "is_public");
    }
  };

  const handleToggleCanDisconnect = async (
    agentUuid: string,
    isCanDisconnect: boolean
  ) => {
    try {
      console.log("handleToggleCanDisconnect", agentUuid, isCanDisconnect);
      // Track processing state for this specific action
      addProcessingState(agentUuid, "can_disconnect");

      await (!isCanDisconnect
        ? disableCanDisconnectAgent(agentUuid)
        : enableCanDisconnectAgent(agentUuid));

      // Update the agents list with the updated can_disconnect status
      setAgents((prev) =>
        prev.map((agent) =>
          agent.uuid === agentUuid
            ? { ...agent, can_disconnect: isCanDisconnect }
            : agent
        )
      );
    } catch (err: any) {
      console.error("Error toggling can disconnect mode:", err);
      setError(
        err.message ||
          `Failed to ${
            isCanDisconnect ? "disable" : "enable"
          } can disconnect mode`
      );
    } finally {
      // Clear processing state for this action
      removeProcessingState(agentUuid, "can_disconnect");
    }
  };

  const handleDeleteAgent = async (agentUuid: string) => {
    if (!window.confirm("Are you sure you want to delete this agent?")) {
      return;
    }

    try {
      addProcessingState(agentUuid, "delete");
      await deleteAgent(agentUuid);
      fetchInitialAgents(debouncedSearch); // Refresh the list from the beginning
    } catch (err: any) {
      console.error("Error deleting agent:", err);
      setError(err.message || "Failed to delete agent");
      removeProcessingState(agentUuid, "delete");
    }
  };

  const handleDuplicateAgent = async (agentUuid: string) => {
    try {
      addProcessingState(agentUuid, "duplicate");
      // Get the full agent details to duplicate
      const agentData = await getAgent(agentUuid);

      // Store the agent data in sessionStorage
      const duplicateData = {
        ...agentData,
        uuid: undefined, // Remove UUID to ensure a new agent is created
        name: `${agentData.name} (Copy)`, // Append "Copy" to name
      };

      sessionStorage.setItem("duplicateAgent", JSON.stringify(duplicateData));
      navigate("/admin/agents/new?duplicate=true");
    } catch (err: any) {
      console.error("Error duplicating agent:", err);
      setError(err.message || "Failed to duplicate agent");
      removeProcessingState(agentUuid, "duplicate");
    }
  };

  const handleCreateDisposableLink = async (
    agentUuid: string,
    agentName: string
  ) => {
    try {
      addProcessingState(agentUuid, "disposable");
      const response = await createDisposableAgentLink(agentUuid);

      if (!response || !response.url) {
        throw new Error("Failed to create disposable link: Invalid response");
      }

      // Auto-copy link to clipboard
      await navigator.clipboard.writeText(response.url);

      setDisposableLinkModal({
        isOpen: true,
        agentUuid,
        agentName,
        link: response.url,
        copied: true, // Set copied to true since we automatically copied it
      });

      // Reset copied state after 1.5 seconds
      setTimeout(() => {
        setDisposableLinkModal((prev) => ({
          ...prev,
          copied: false,
        }));
      }, 1500);

      removeProcessingState(agentUuid, "disposable");
    } catch (err: any) {
      console.error("Error creating disposable link:", err);
      setError(err.message || "Failed to create disposable link");
      removeProcessingState(agentUuid, "disposable");
    }
  };

  const copyDisposableLink = () => {
    navigator.clipboard.writeText(disposableLinkModal.link);
    setDisposableLinkModal((prev) => ({
      ...prev,
      copied: true,
    }));

    // Reset copied state after 1.5 seconds
    setTimeout(() => {
      setDisposableLinkModal((prev) => ({
        ...prev,
        copied: false,
      }));
    }, 1500);
  };

  const closeDisposableLinkModal = () => {
    setDisposableLinkModal((prev) => ({
      ...prev,
      isOpen: false,
    }));
  };

  const navigateToCreate = () => {
    navigate("/admin/agents/new");
  };

  const navigateToTags = () => {
    navigate("/admin/tags");
  };

  const copyAccessToken = async (agentUuid: string, token: string) => {
    try {
      addProcessingState(agentUuid, "copy_token");
      await navigator.clipboard.writeText(token);

      // Set copied state for this specific agent
      setTokenCopied((prev) => ({
        ...prev,
        [agentUuid]: true,
      }));

      // Reset after 1.5 seconds
      setTimeout(() => {
        setTokenCopied((prev) => ({
          ...prev,
          [agentUuid]: false,
        }));
        removeProcessingState(agentUuid, "copy_token");
      }, 1500);
    } catch (err) {
      console.error("Failed to copy token", err);
      removeProcessingState(agentUuid, "copy_token");
    }
  };

  const handleShareAgent = (
    e: React.MouseEvent,
    agentUuid: string,
    accessToken: string | undefined
  ) => {
    e.stopPropagation();
    e.preventDefault();

    try {
      addProcessingState(agentUuid, "share_link");
      // Include access token in the link if available
      let link = `${window.location.origin}/access?agent=${agentUuid}`;
      if (accessToken) {
        link += `&token=${accessToken}`;
      }

      navigator.clipboard
        .writeText(link)
        .then(() => {
          setSharedAgent(agentUuid);
          setTimeout(() => {
            setSharedAgent(null);
            removeProcessingState(agentUuid, "share_link");
          }, 2000);
        })
        .catch((err) => {
          console.error("Failed to copy link: ", err);
          removeProcessingState(agentUuid, "share_link");
        });
    } catch (err) {
      console.error("Error creating share link:", err);
      removeProcessingState(agentUuid, "share_link");
    }
  };

  if (loading) {
    return (
      <div className="container mx-auto py-8 px-4">
        <LoadingState message="Loading agents..." />
      </div>
    );
  }

  return (
    <div className="container mx-auto py-8 px-4 max-w-6xl">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold text-[#140d0c]">Agent Configuration</h1>
        <div className="flex-1 mx-6">
          <input
            type="text"
            placeholder="Search agents by name..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        </div>
        <div className="flex gap-3">
          <button
            onClick={navigateToTags}
            className="px-4 py-2 bg-[#1f87e7] text-white rounded hover:bg-[#1c7fdf] transition-colors flex items-center gap-1"
          >
            <HiOutlineTag className="mr-2" />
            Manage Tags
          </button>
          <button
            onClick={navigateToCreate}
            className="px-4 py-2 bg-[#ffcc33] text-[#140d0c] rounded hover:bg-[#ffcc33]/90 transition-colors flex items-center gap-1"
          >
            <HiPlus className="mr-2" />
            Add New Agent
          </button>
        </div>
      </div>

      <div className="bg-white shadow-md rounded-lg p-6">
        {error && (
          <div className="p-3 bg-red-50 border border-red-200 text-red-600 rounded-md text-sm mb-4">
            {error}
          </div>
        )}

        {/* Global loading indicator - fixed position to avoid layout shifts */}
        {loading && agents.length > 0 && (
          <div className="fixed top-4 right-4 z-50 bg-white rounded-lg shadow-md p-2">
            <LoadingState
              size="small"
              padding="none"
              message=""
              fullPage={false}
            />
          </div>
        )}

        <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
          {agents.map((agent) => {
            return (
              <div
                key={agent.uuid}
                className="bg-white rounded-lg shadow-md p-6 flex flex-col border border-[#e7e2d3] hover:shadow-lg transition-all duration-300"
              >
                <div className="flex flex-col gap-3 mb-4">
                  <div className="flex items-center justify-between">
                    <h3 className="text-lg font-medium text-[#140d0c]">
                      {agent.name}
                    </h3>
                    <span
                      className={`px-2 py-1 text-xs rounded-full ${
                        agent.is_outbound
                          ? "bg-blue-100 text-blue-800"
                          : "bg-secondary text-secondary-foreground"
                      }`}
                    >
                      {agent.is_outbound ? "Outbound" : "Inbound"}
                    </span>
                  </div>

                  <div className="flex flex-wrap gap-2">
                    <button
                      onClick={() => {
                        if (!isProcessing(agent.uuid, "enable")) {
                          handleToggleAgent(agent.uuid, !agent.enabled);
                        }
                      }}
                      disabled={isProcessing(agent.uuid, "enable")}
                      className={`flex items-center justify-center px-3 py-1.5 text-xs font-medium rounded-full min-w-[80px] shadow-sm border transition-all ${
                        agent.enabled
                          ? "bg-green-50 text-green-700 border-green-300 hover:bg-green-100"
                          : "bg-red-50 text-red-700 border-red-300 hover:bg-red-100"
                      } ${
                        isProcessing(agent.uuid, "enable")
                          ? "opacity-70 cursor-not-allowed"
                          : ""
                      }`}
                    >
                      {isProcessing(agent.uuid, "enable") && (
                        <LoadingState
                          size="small"
                          padding="none"
                          message="Processing..."
                        />
                      )}
                      {!isProcessing(agent.uuid, "enable") && agent.enabled && (
                        <span className="flex items-center">
                          <svg
                            className="w-3 h-3 mr-1"
                            fill="currentColor"
                            viewBox="0 0 20 20"
                          >
                            <path
                              fillRule="evenodd"
                              d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                              clipRule="evenodd"
                            ></path>
                          </svg>
                          Enabled
                        </span>
                      )}
                      {!isProcessing(agent.uuid, "enable") &&
                        !agent.enabled && (
                          <span className="flex items-center">
                            <svg
                              className="w-3 h-3 mr-1"
                              fill="currentColor"
                              viewBox="0 0 20 20"
                            >
                              <path
                                fillRule="evenodd"
                                d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                                clipRule="evenodd"
                              ></path>
                            </svg>
                            Disabled
                          </span>
                        )}
                    </button>
                    <button
                      onClick={() => {
                        if (!isProcessing(agent.uuid, "is_public")) {
                          handleToggleIsPublic(agent.uuid, !agent.is_public);
                        }
                      }}
                      disabled={isProcessing(agent.uuid, "is_public")}
                      className={`flex items-center justify-center px-3 py-1.5 text-xs font-medium rounded-full min-w-[80px] shadow-sm border transition-all ${
                        agent.is_public
                          ? "bg-indigo-50 text-indigo-700 border-indigo-300 hover:bg-indigo-100"
                          : "bg-gray-50 text-gray-700 border-gray-300 hover:bg-gray-100"
                      } ${
                        isProcessing(agent.uuid, "is_public")
                          ? "opacity-70 cursor-not-allowed"
                          : ""
                      }`}
                    >
                      {isProcessing(agent.uuid, "is_public") && (
                        <LoadingState
                          size="small"
                          padding="none"
                          message="Processing..."
                        />
                      )}
                      {!isProcessing(agent.uuid, "is_public") &&
                        agent.is_public && (
                          <span className="flex items-center">
                            <HiEye className="w-3 h-3 mr-1" />
                            Public
                          </span>
                        )}
                      {!isProcessing(agent.uuid, "is_public") &&
                        !agent.is_public && (
                          <span className="flex items-center">
                            <HiEyeOff className="w-3 h-3 mr-1" />
                            Private
                          </span>
                        )}
                    </button>
                    <button
                      onClick={() => {
                        if (!isProcessing(agent.uuid, "can_disconnect")) {
                          handleToggleCanDisconnect(
                            agent.uuid,
                            !agent.can_disconnect
                          );
                        }
                      }}
                      disabled={isProcessing(agent.uuid, "can_disconnect")}
                      className={`flex items-center justify-center px-3 py-1.5 text-xs font-medium rounded-full min-w-[80px] shadow-sm border transition-all ${
                        agent.can_disconnect
                          ? "bg-green-50 text-green-700 border-green-300 hover:bg-green-100"
                          : "bg-red-50 text-red-700 border-red-300 hover:bg-red-100"
                      } ${
                        isProcessing(agent.uuid, "can_disconnect")
                          ? "opacity-70 cursor-not-allowed"
                          : ""
                      }`}
                    >
                      {isProcessing(agent.uuid, "can_disconnect") && (
                        <LoadingState
                          size="small"
                          padding="none"
                          message="Processing..."
                        />
                      )}
                      {!isProcessing(agent.uuid, "can_disconnect") &&
                        agent.can_disconnect && (
                          <span className="flex items-center">
                            <svg
                              className="w-3 h-3 mr-1"
                              fill="currentColor"
                              viewBox="0 0 20 20"
                            >
                              <path
                                fillRule="evenodd"
                                d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                                clipRule="evenodd"
                              ></path>
                            </svg>
                            Can Disconnect
                          </span>
                        )}
                      {!isProcessing(agent.uuid, "can_disconnect") &&
                        !agent.can_disconnect && (
                          <span className="flex items-center">
                            <svg
                              className="w-3 h-3 mr-1"
                              fill="currentColor"
                              viewBox="0 0 20 20"
                            >
                              <path
                                fillRule="evenodd"
                                d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                                clipRule="evenodd"
                              ></path>
                            </svg>
                            Can't Disconnect
                          </span>
                        )}
                    </button>
                  </div>
                </div>

                <p className="text-[#6c6c6c] mb-4">{agent.description}</p>

                <div className="mb-4 space-y-3">
                  <div className="flex flex-col gap-1">
                    <div className="text-xs text-[#6c6c6c]">
                      <div className="grid grid-cols-2 gap-1">
                        <p>
                          <span className="text-gray-500">UUID:</span>{" "}
                          {agent.uuid}
                        </p>
                        <p>
                          <span className="text-gray-500">Type:</span>{" "}
                          {agent.primary_language}
                        </p>
                      </div>
                    </div>

                    {agent.tags && agent.tags.length > 0 && (
                      <div className="flex flex-wrap gap-1 mt-1">
                        {agent.tags.map((tag, index) => (
                          <span
                            key={index}
                            className="px-2 py-0.5 bg-gray-100 text-gray-600 rounded-full text-xs"
                          >
                            {tag}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>

                  {/* Display phone number if exists */}
                  {agent.phone_number && (
                    <div className="bg-white p-3 rounded-md border border-green-100 shadow-sm">
                      <div className="flex flex-col">
                        <div className="flex items-center mb-2">
                          <HiPhone className="w-4 h-4 mr-1.5 text-green-600" />
                          <span className="font-semibold text-gray-700 text-sm">
                            Phone Number
                          </span>
                        </div>
                        <div className="flex items-center">
                          <div className="flex-1 px-3 py-2 bg-green-50 text-green-700 rounded-md font-mono text-sm border border-green-200 flex items-center justify-between">
                            <span>{agent.phone_number}</span>
                            {agent.is_outbound && (
                              <span className="ml-2 text-xs text-green-600 bg-green-100 px-2 py-0.5 rounded-full">
                                outbound caller ID
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Display access token if exists */}
                  {agent.access_token && (
                    <div className="bg-white p-3 rounded-md border border-blue-100 shadow-sm">
                      <div className="flex flex-col">
                        <div className="flex items-center mb-2">
                          <svg
                            className="w-4 h-4 mr-1.5 text-blue-600"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z"
                            />
                          </svg>
                          <span className="font-semibold text-gray-700 text-sm">
                            Access Token
                          </span>
                        </div>
                        <div className="flex items-center">
                          <div className="flex-1 px-3 py-2 bg-blue-50 text-blue-700 rounded-l-md font-mono text-sm border border-blue-200 truncate">
                            {tokenCopied[agent.uuid]
                              ? agent.access_token
                              : agent.access_token?.substring(0, 4) +
                                "••••••" +
                                agent.access_token?.substring(
                                  agent.access_token.length - 4
                                )}
                          </div>
                          <button
                            onClick={() =>
                              copyAccessToken(
                                agent.uuid,
                                agent.access_token || ""
                              )
                            }
                            disabled={isProcessing(agent.uuid, "copy_token")}
                            className={`px-4 py-2 rounded-r-md flex items-center border border-l-0 ${
                              tokenCopied[agent.uuid]
                                ? "bg-green-500 text-white border-green-500"
                                : "bg-blue-500 text-white border-blue-500 hover:bg-blue-600"
                            } transition-colors`}
                            title="Copy token to clipboard"
                          >
                            {isProcessing(agent.uuid, "copy_token") &&
                            !tokenCopied[agent.uuid] ? (
                              <span className="inline-flex items-center">
                                <svg
                                  className="animate-spin h-4 w-4 mr-1.5"
                                  viewBox="0 0 24 24"
                                >
                                  <circle
                                    className="opacity-25"
                                    cx="12"
                                    cy="12"
                                    r="10"
                                    stroke="currentColor"
                                    strokeWidth="4"
                                    fill="none"
                                  ></circle>
                                  <path
                                    className="opacity-75"
                                    fill="currentColor"
                                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                                  ></path>
                                </svg>
                              </span>
                            ) : tokenCopied[agent.uuid] ? (
                              <>
                                <HiCheck className="w-4 h-4 mr-1.5" />
                                <span>Copied!</span>
                              </>
                            ) : (
                              <>
                                <HiOutlineClipboard className="w-4 h-4 mr-1.5" />
                                <span>Copy</span>
                              </>
                            )}
                          </button>
                        </div>
                      </div>
                    </div>
                  )}
                </div>

                <div className="mt-auto pt-4 border-t border-[#e7e2d3] flex flex-wrap justify-end gap-2">
                  <button
                    onClick={(e) =>
                      handleShareAgent(e, agent.uuid, agent.access_token)
                    }
                    disabled={isProcessing(agent.uuid, "share_link")}
                    className={`px-3 py-1.5 bg-indigo-500 text-white rounded hover:bg-indigo-600 transition-colors flex items-center gap-1.5 text-sm ${
                      isProcessing(agent.uuid, "share_link") && !sharedAgent
                        ? "opacity-50 cursor-not-allowed"
                        : sharedAgent === agent.uuid
                        ? "bg-green-500 hover:bg-green-600"
                        : ""
                    }`}
                    title="Share direct access link with token"
                  >
                    {isProcessing(agent.uuid, "share_link") && !sharedAgent ? (
                      <span className="inline-flex items-center">
                        <svg
                          className="animate-spin h-3.5 w-3.5 mr-1.5"
                          viewBox="0 0 24 24"
                        >
                          <circle
                            className="opacity-25"
                            cx="12"
                            cy="12"
                            r="10"
                            stroke="currentColor"
                            strokeWidth="4"
                            fill="none"
                          ></circle>
                          <path
                            className="opacity-75"
                            fill="currentColor"
                            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                          ></path>
                        </svg>
                      </span>
                    ) : sharedAgent === agent.uuid ? (
                      <>
                        <HiCheck className="w-4 h-4 mr-1.5" />
                        <span>Copied!</span>
                      </>
                    ) : (
                      <>
                        <HiOutlineShare className="w-4 h-4 mr-1.5" />
                        <span>Share Link</span>
                      </>
                    )}
                  </button>
                  <button
                    onClick={() => {
                      if (!isProcessing(agent.uuid, "disposable")) {
                        handleCreateDisposableLink(agent.uuid, agent.name);
                      }
                    }}
                    disabled={isProcessing(agent.uuid, "disposable")}
                    className={`px-3 py-1.5 bg-accent text-accent-foreground rounded hover:bg-accent/90 transition-colors flex items-center gap-1.5 text-sm ${
                      isProcessing(agent.uuid, "disposable")
                        ? "opacity-50 cursor-not-allowed"
                        : ""
                    }`}
                  >
                    {isProcessing(agent.uuid, "disposable") ? (
                      <span className="inline-flex items-center">
                        <svg
                          className="animate-spin h-3.5 w-3.5 mr-1.5"
                          viewBox="0 0 24 24"
                        >
                          <circle
                            className="opacity-25"
                            cx="12"
                            cy="12"
                            r="10"
                            stroke="currentColor"
                            strokeWidth="4"
                            fill="none"
                          ></circle>
                          <path
                            className="opacity-75"
                            fill="currentColor"
                            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                          ></path>
                        </svg>
                      </span>
                    ) : (
                      <HiOutlineLink className="w-4 h-4 mr-1.5" />
                    )}
                    Generate Link
                  </button>
                  <button
                    onClick={() => navigate(`/admin/agents/${agent.uuid}`)}
                    disabled={processingStates.some(
                      (state) => state.uuid === agent.uuid
                    )}
                    className={`px-3 py-1.5 bg-blue-500 text-white rounded hover:bg-blue-600 transition-colors flex items-center gap-1.5 text-sm ${
                      processingStates.some(
                        (state) => state.uuid === agent.uuid
                      )
                        ? "opacity-50 cursor-not-allowed"
                        : ""
                    }`}
                  >
                    <svg
                      className="w-4 h-4 mr-1"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z"
                      />
                    </svg>
                    Edit
                  </button>
                  <button
                    onClick={() => {
                      if (!isProcessing(agent.uuid, "duplicate")) {
                        handleDuplicateAgent(agent.uuid);
                      }
                    }}
                    disabled={isProcessing(agent.uuid, "duplicate")}
                    className={`px-3 py-1.5 bg-green-500 text-white rounded hover:bg-green-600 transition-colors flex items-center gap-1.5 text-sm ${
                      isProcessing(agent.uuid, "duplicate")
                        ? "opacity-50 cursor-not-allowed"
                        : ""
                    }`}
                  >
                    {isProcessing(agent.uuid, "duplicate") ? (
                      <span className="inline-flex items-center">
                        <svg
                          className="animate-spin h-3.5 w-3.5 mr-1.5"
                          viewBox="0 0 24 24"
                        >
                          <circle
                            className="opacity-25"
                            cx="12"
                            cy="12"
                            r="10"
                            stroke="currentColor"
                            strokeWidth="4"
                            fill="none"
                          ></circle>
                          <path
                            className="opacity-75"
                            fill="currentColor"
                            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                          ></path>
                        </svg>
                      </span>
                    ) : (
                      <svg
                        className="w-4 h-4 mr-1"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z"
                        />
                      </svg>
                    )}
                    Duplicate
                  </button>
                  <button
                    onClick={() => {
                      if (!isProcessing(agent.uuid, "delete")) {
                        handleDeleteAgent(agent.uuid);
                      }
                    }}
                    disabled={isProcessing(agent.uuid, "delete")}
                    className={`px-3 py-1.5 bg-red-500 text-white rounded hover:bg-red-600 transition-colors flex items-center gap-1.5 text-sm ${
                      isProcessing(agent.uuid, "delete")
                        ? "opacity-50 cursor-not-allowed"
                        : ""
                    }`}
                  >
                    {isProcessing(agent.uuid, "delete") ? (
                      <span className="inline-flex items-center">
                        <svg
                          className="animate-spin h-3.5 w-3.5 mr-1.5"
                          viewBox="0 0 24 24"
                        >
                          <circle
                            className="opacity-25"
                            cx="12"
                            cy="12"
                            r="10"
                            stroke="currentColor"
                            strokeWidth="4"
                            fill="none"
                          ></circle>
                          <path
                            className="opacity-75"
                            fill="currentColor"
                            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                          ></path>
                        </svg>
                      </span>
                    ) : (
                      <svg
                        className="w-4 h-4 mr-1"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                        />
                      </svg>
                    )}
                    Delete
                  </button>
                </div>
              </div>
            );
          })}
        </div>

        {/* Infinite scroll sentinel and loader */}
        <div ref={sentinelRef} className="h-4" />
        {isFetchingMore && (
          <div className="flex justify-center items-center py-4">
            <LoadingState message="Loading more agents..." fullPage={false} />
          </div>
        )}

        {agents.length === 0 && !loading && (
          <div className="text-center py-8">
            <p className="text-[#6c6c6c]">No agents found.</p>
            <button
              onClick={() => navigate("/admin/agents/new")}
              className="mt-4 px-4 py-2 bg-[#ffcc33] text-[#140d0c] rounded hover:bg-[#ffcc33]/90 transition-colors"
            >
              Create Your First Agent
            </button>
          </div>
        )}
      </div>

      {/* Disposable Link Modal */}
      {disposableLinkModal.isOpen && (
        <div className="fixed inset-0 flex items-center justify-center bg-black/50 z-50">
          <div className="bg-white rounded-lg shadow-lg w-full max-w-lg p-6 m-4">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-medium">
                Upload Link for {disposableLinkModal.agentName}
              </h3>
              <button
                onClick={closeDisposableLinkModal}
                className="text-gray-500 hover:text-gray-700"
              >
                <HiOutlineX className="w-5 h-5" />
              </button>
            </div>
            <p className="text-sm text-gray-600 mb-4">
              Share this link with your client to allow them to upload knowledge
              base files for this agent.
              <br />
              <br />
              <span className="text-red-600 font-medium">Important:</span> This
              link will only work once. After a successful upload, it will
              become invalid.
            </p>
            <div className="flex items-center mb-4">
              <input
                type="text"
                value={disposableLinkModal.link}
                readOnly
                className="flex-grow px-3 py-2 border border-gray-300 rounded-l-md bg-gray-50 text-sm"
              />
              <button
                onClick={copyDisposableLink}
                className={`px-3 py-2 rounded-r-md flex items-center ${
                  disposableLinkModal.copied
                    ? "bg-green-500 text-white"
                    : "bg-blue-500 text-white hover:bg-blue-600"
                }`}
              >
                {disposableLinkModal.copied ? (
                  <>
                    <HiCheck className="w-4 h-4 mr-1" />
                    Copied
                  </>
                ) : (
                  <>
                    <HiOutlineClipboard className="w-4 h-4 mr-1" />
                    Copy
                  </>
                )}
              </button>
            </div>
            <div className="flex justify-end">
              <button
                onClick={closeDisposableLinkModal}
                className="px-4 py-2 bg-gray-200 text-gray-800 rounded hover:bg-gray-300 transition-colors"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AgentConfiguration;
